import * as farmApi from 'Api/farm';
import { httpClientInvestment, httpClientFarm } from 'Api/microservice-http-clients';
import { noAuthWSClient } from 'WSClient';
import { isNotEmptyObject } from 'Utils/common';
import { FarmClass } from 'Classes/FarmClass';
import { SET_ALL, RESET_FARM_DATA, UPDATE_FARM_DATA, SET_DOCUMENT_AS_READ, SET_FINANCIAL_STATEMENTS_DOCUMENT_AS_READ } from './mutation-types';

export default {
  getList({ commit, getters, state, dispatch }, { limit, refresh } = {}) {
    if (!refresh) commit(SET_ALL, { listLoading: true, listLoaded: false });
    const listLimit = limit || (refresh ? state.listLimit : undefined);
    return farmApi.getList(listLimit, getters.farmSortValue)
      .then(({ data }) => {
        const list = data.data?.map(item => new FarmClass(item)) || [];
        commit(SET_ALL, {
          list,
          listCount: data.count,
          listLoading: false,
          listLoaded: true,
          listLimit,
        });
        if (!refresh) dispatch('setupFarmListNotification');
        return data;
      })
      .catch((error) => {
        commit(SET_ALL, { listLoading: false });
        return error;
      });
  },
  getClosedSuccessfullyList({ commit }, { limit = 1000 } = {}) {
    const sort = 'recently-closed:desc';
    commit(SET_ALL, { closedListLoading: true });
    return farmApi.getClosedSuccessfullyList(limit, sort)
      .then(({ data }) => {
        const closedSuccessfullyList = data.data?.map(item => new FarmClass(item)) || [];
        commit(SET_ALL, {
          closedSuccessfullyList,
          closedSuccessfullyListCount: data.count,
          closedSuccessfullyListLoading: false,
        });
        return data;
      })
      .catch((error) => {
        commit(SET_ALL, { closedListLoading: false });
        return error;
      });
  },
  /**
   * Reset farm data
   * @param context
   * @param farmData - leave as farm data after reset
   */
  resetFarmData({ commit, dispatch }, farmData) {
    dispatch('removeFarmNotification');
    commit(RESET_FARM_DATA, farmData);
  },
  async getFarmData({ commit, dispatch, getters }, { slug = getters.farmData.slug, force, refresh }) {
    if (getters.farmData.slug === slug && !force && !refresh && getters.farmData.id) {
      return dispatch('getFarmOptions', getters.farmData.id);
    }
    commit(SET_ALL, {
      farmDataLoading: !refresh, // Do not change state variable if dat refreshing
      farmDataLoaded: refresh,
    });
    try {
      const { data } = await farmApi.getFarmData(slug);
      // If getting another farm
      if (getters.farmData.slug !== slug) {
        // Reset farm data
        dispatch('resetFarmData', { slug });
      }
      const farmData = new FarmClass(data);
      commit(SET_ALL, {
        farmData,
        farmDataLoading: false,
        farmDataLoaded: true,
      });
      // If it not refreshing
      if (!refresh) {
        // Setup notification
        dispatch('setupFarmNotification');
        // And get farm options
        await dispatch('getFarmOptions', data.id);
      }
      return Promise.resolve(data);
    } catch (e) {
      commit(SET_ALL, { farmDataLoading: false });
      throw e;
    }
  },
  getFarmOptions({ dispatch }, farmId) {
    const promises = [];
    promises.push(dispatch('getFarmUnfinishedInvestment', farmId));
    return Promise.all(promises);
  },
  getFarmUnfinishedInvestment({ commit, rootGetters }, farmId) {
    // Return if user is not login
    if (!rootGetters['login/isLoggedIn']) return Promise.resolve();
    commit(SET_ALL, { farmUnfinishedInvestmentLoading: true });
    return httpClientInvestment.get(`/unfinished/${farmId}`)
      .then(({ data }) => {
        commit(SET_ALL, {
          farmUnfinishedInvestment: data,
          farmUnfinishedInvestmentLoading: false,
        });
        return data;
      })
      .catch(() => {
        commit(SET_ALL, {
          farmUnfinishedInvestment: {},
          farmUnfinishedInvestmentLoading: false,
        });
      });
  },
  setFarmSortValue({ commit }, farmSortValue) {
    commit(SET_ALL, { listLoading: true });
    commit(SET_ALL, { farmSortValue, listLoading: false });
  },
  resetFarmOnLogout({ commit }) {
    commit(SET_ALL, {
      isUserWatch: false,
      isUserWatchLoading: false,
      farmUnfinishedInvestment: {},
      farmUnfinishedInvestmentLoading: false,
    });
  },
  setupFarmListNotification({ dispatch, commit, state }) {
    if (state.farmListSubscribed) return;
    noAuthWSClient.subscribe(
      'refresh.farm.list', {},
      () => {
        dispatch('getList', { refresh: true });
        dispatch('getClosedSuccessfullyList');
      },
    );
    commit(SET_ALL, { farmListSubscribed: true });
  },
  setupFarmNotification({ getters, dispatch }, farmId = getters.farmData.id) {
    noAuthWSClient.subscribe(
      `invest.2.farm.${farmId}`, {},
      message => dispatch('handleFarmNotification', message),
    );
  },
  removeFarmNotification({ getters }, farmId = getters.farmData.id) {
    if (!farmId) return;
    noAuthWSClient.unsubscribe(`invest.2.farm.${farmId}`);
  },
  handleFarmNotification({ commit, getters, dispatch }, { channel, message }) {
    const farmId = getters.farmData.id;
    // Handle messages only if it for current farm
    if (channel.endsWith(String(farmId))) {
      // If something in message, update farm data
      if (message && isNotEmptyObject(message)) {
        commit(UPDATE_FARM_DATA, message);
      } else {
        // Otherwise refresh all farm data
        dispatch('getFarmData', { refresh: true });
      }
    }
  },
  setDocumentAsRead({ getters, commit }, { fileId, code }) {
    const farmId = getters.farmData.id;
    return httpClientFarm.post(`/${farmId}/read`, { filer_id: fileId })
      .then(() => {
        if (code === 'financial') commit(SET_FINANCIAL_STATEMENTS_DOCUMENT_AS_READ, fileId);
        else commit(SET_DOCUMENT_AS_READ, fileId);
      })
      .catch(error => error);
  },
};
